import React, { useState, useEffect } from 'react'
import {
  Zoning,
  ZoningType,
  ZoningReference,
  ZoningQALanguage,
  ZoneDocumentsGroupedResponse,
} from '@popety-io/types'
import { Icon } from '../../../atoms'
import { MetricSourceBadge } from '../../../atoms/MetricSourceBadge'
import { StatusBadge } from './StatusBadge'
import { MetricSourceModal } from '../../../molecules/MetricSourceModal'
import { ZoneQAModal } from '../../../molecules/ZoneQAModal'
import { DocumentsSection } from './DocumentsSection'
import { ReferencesSection, ReferenceViewerModal } from './ReferencesSection'
import type { ZoneQAService, ZoneQATranslations } from './ZoneQA'
import {
  getZonePlanTypeTitle,
  getZoneNameForHeader,
  getZoneStatus,
  formatSwissDate,
  getZoneId,
} from '../utils/zoningDisplayHelpers'
import {
  HeaderContainer,
  BannerSection,
  BannerLeft,
  BannerRight,
  PlanTypeTitle,
  ZoneName,
  ZoneNameRow,
  CoveragePill,
  CoverageDot,
  CoverageSuffix,
  BadgesRow,
  AdoptionDate,
  RegulationCard,
  RegulationLabel,
  RegulationValue,
  RegulationBody,
  RegulationChevron,
  QAButton,
  SegmentBar,
  SegmentGroup,
  Segment,
  SegmentCount,
  PaneWrap,
  Pane,
  PotentialPane,
  TrailingContext,
  SectionDivider,
  SectionLabel,
  SectionLabelText,
  SectionCount,
  SectionRule,
  LegendSlot,
} from './ZoningDetailHeader.styles'

export interface ZoningDetailHeaderTranslations {
  unknownZone?: string
  status?: {
    current?: string
    future?: string
    past?: string
  }
  buildingArea?: string
  nonBuildingArea?: string
  adopted?: string
  abrogated?: string
  purpose?: string
  constructionArrangement?: string
  constructionArrangementValues?: {
    C?: string
    NC?: string
    'C / NC'?: string
  }
  /** Q&A button label */
  askQuestion?: string
  /** Tab labels */
  references?: string
  documents?: string
  /** Segmented-card toolbar labels */
  segments?: {
    potential?: string
    purposeRules?: string
    documents?: string
  }
  /** Suffix shown after the percentage in the plot-coverage pill (e.g., "of plot") */
  plotCoverage?: string
}

export interface DocumentTranslations {
  title?: string
  loading?: string
  noDocuments?: string
  view?: string
  modalTitle?: string
  searchPlaceholder?: string
  searchAriaLabel?: string
  clearSearchAriaLabel?: string
  noDocumentSelected?: string
  selectDocumentHint?: string
  important?: string
  noPreviewAvailable?: string
  noPreviewHint?: string
  documentTypes?: {
    rule?: string
    map?: string
  }
  sourceLabels?: {
    lupa?: string
    lupc?: string
    lupo?: string
    lup?: string
    region?: string
    country?: string
  }
  sourceTitles?: {
    lupa?: string
    lupc?: string
    lupo?: string
    lup?: string
    region?: string
    country?: string
  }
}

const DEFAULT_HEADER_TRANSLATIONS: ZoningDetailHeaderTranslations = {
  unknownZone: 'Unknown zone',
  status: {
    current: 'Current',
    future: 'Future',
    past: 'Past',
  },
  buildingArea: 'Building Area',
  nonBuildingArea: 'Non-Building Area',
  adopted: 'Adopted',
  abrogated: 'Abrogated',
  purpose: 'Purpose',
  constructionArrangement: 'Construction Arrangement',
  constructionArrangementValues: {
    C: 'Contiguous',
    NC: 'Non-contiguous',
    'C / NC': 'Contiguous / Non-contiguous',
  },
  askQuestion: 'Ask me anything about this zone',
  references: 'References',
  documents: 'Documents',
  segments: {
    potential: 'Development potential',
    purposeRules: 'Purpose & rules',
    documents: 'Documents',
  },
  plotCoverage: 'of plot',
}

export interface ZoningDetailHeaderProps {
  zone: Zoning
  zoningType: ZoningType
  zoningService?: any
  translations?: ZoningDetailHeaderTranslations
  documentTranslations?: DocumentTranslations
  /** Translations for the reference viewer modal (title, search, page label) */
  referenceViewerTranslations?: import('./ReferencesSection/ReferenceViewerModal').ReferenceViewerModalTranslations
  /** Backend metric key → translated label (e.g. distance_limit → "Distance limite") */
  metricLabelMap?: Record<string, string>
  locale?: string
  zoneQAService?: ZoneQAService
  zoneQATranslations?: ZoneQATranslations
  onViewDocs?: () => void
  onTranscribeVoice?: (audioBlob: Blob) => Promise<string>
  /** Optional: transform a PDF URL through a proxy to bypass CORS */
  getPdfProxyUrl?: (url: string) => string
  /** Share of the plot covered by this zone (decimal 0..1) */
  plotCoveragePerc?: number
  /** Rendered content for the "Development potential" segment (metrics table). */
  potentialSlot?: React.ReactNode
  /** Metrics legend; shown at the bottom of the Development potential pane
   *  only (it explains the metric badges, so it stays out of Documents). */
  legendSlot?: React.ReactNode
}

type CardSegment = 'potential' | 'documents'

export const ZoningDetailHeader: React.FC<ZoningDetailHeaderProps> = ({
  zone,
  zoningType,
  zoningService,
  translations,
  documentTranslations,
  referenceViewerTranslations,
  metricLabelMap,
  locale,
  zoneQAService,
  zoneQATranslations,
  onViewDocs: _onViewDocs,
  onTranscribeVoice,
  getPdfProxyUrl,
  plotCoveragePerc,
  potentialSlot,
  legendSlot,
}) => {
  const t = {
    ...DEFAULT_HEADER_TRANSLATIONS,
    ...translations,
    status: {
      ...DEFAULT_HEADER_TRANSLATIONS.status,
      ...translations?.status,
    },
    constructionArrangementValues: {
      ...DEFAULT_HEADER_TRANSLATIONS.constructionArrangementValues,
      ...translations?.constructionArrangementValues,
    },
    segments: {
      ...DEFAULT_HEADER_TRANSLATIONS.segments,
      ...translations?.segments,
    },
  }

  const [modalState, setModalState] = useState<{
    isOpen: boolean
    field: 'purpose' | 'construction_arrangement' | null
    reference: ZoningReference | null
    value?: string
    defaultSource?: string
    sourcesAgreement?: string
    sources?: any
  }>({ isOpen: false, field: null, reference: null })
  const [isQAModalOpen, setIsQAModalOpen] = useState(false)
  const [zoneDocuments, setZoneDocuments] =
    useState<ZoneDocumentsGroupedResponse | null>(null)
  const [documentCount, setDocumentCount] = useState<number | null>(null)

  const hasReferences = zone.references && zone.references.length > 0
  const [activeSegment, setActiveSegment] = useState<CardSegment>('potential')
  const [refModalState, setRefModalState] = useState<{
    isOpen: boolean
    initialDocumentId?: number
    initialPage?: number
  }>({ isOpen: false })

  useEffect(() => {
    const fetchDocuments = async () => {
      const zoneId = zone._id
      if (!zoneId || !zoningService?.getZoneDocuments) {
        return
      }

      try {
        const response = await zoningService.getZoneDocuments(
          zoningType,
          zoneId
        )
        setZoneDocuments(response)
      } catch (error) {
        console.error('Error fetching zone documents for Q&A:', error)
      }
    }

    fetchDocuments()
  }, [zone._id, zoningType, zoningService])

  const planType = getZonePlanTypeTitle(zone, zoningType, t.unknownZone)
  const zoneName = getZoneNameForHeader(zone, zoningType, t.unknownZone)
  const status = getZoneStatus(zone, t.status)
  const adoptionDate = formatSwissDate(zone.adoption_date)
  const abrogationDate = formatSwissDate(zone.abrogation_date)

  const hasBuildingAreaInfo = zone.building_area !== undefined
  const hasDateInfo = adoptionDate || abrogationDate
  const purposeText = zone.purpose_value || zone.purpose
  const hasPurpose = Boolean(purposeText)
  const rawArrangement =
    zone.construction_arrangement_value ||
    (zone.construction_arrangement != null
      ? String(zone.construction_arrangement)
      : undefined)
  const constructionArrangementText = rawArrangement
    ? t.constructionArrangementValues?.[
        rawArrangement as keyof typeof t.constructionArrangementValues
      ] || rawArrangement
    : undefined

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      field: null,
      reference: null,
      value: undefined,
      defaultSource: undefined,
      sourcesAgreement: undefined,
      sources: undefined,
    })
  }

  const coveragePercent =
    typeof plotCoveragePerc === 'number' && plotCoveragePerc > 0
      ? Math.min(100, Math.max(1, Math.round(plotCoveragePerc * 100)))
      : null
  const coverageVariant: 'high' | 'medium' | 'low' | null =
    coveragePercent == null
      ? null
      : coveragePercent >= 25
        ? 'high'
        : coveragePercent >= 10
          ? 'medium'
          : 'low'

  // Which segments the toolbar exposes. "Development potential" (the metrics
  // table) is always present; the others appear only when they have content.
  const showPurposeRules =
    hasPurpose || Boolean(constructionArrangementText) || Boolean(hasReferences)
  const showDocuments = Boolean(zoningService)
  const showQA =
    zoningType === 'general' &&
    Boolean(zoneQAService) &&
    Boolean(zone.municipality_id) &&
    Boolean(zone.region_id)

  return (
    <HeaderContainer>
      {/* Full-width banner */}
      <BannerSection>
        <BannerLeft>
          <ZoneNameRow>
            <PlanTypeTitle>{planType}</PlanTypeTitle>
            {coveragePercent != null && coverageVariant && (
              <CoveragePill $variant={coverageVariant}>
                <CoverageDot $variant={coverageVariant} />
                {coveragePercent}%
                <CoverageSuffix>{t.plotCoverage || 'of plot'}</CoverageSuffix>
              </CoveragePill>
            )}
          </ZoneNameRow>
          <ZoneName>{zoneName}</ZoneName>
        </BannerLeft>
        <BannerRight>
          <BadgesRow>
            {status && (
              <StatusBadge variant={status.variant} label={status.label} />
            )}
            {hasBuildingAreaInfo && (
              <StatusBadge
                variant={
                  zone.building_area ? 'buildingArea' : 'nonBuildingArea'
                }
                label={
                  zone.building_area
                    ? t.buildingArea || 'Building Area'
                    : t.nonBuildingArea || 'Non-Building Area'
                }
              />
            )}
          </BadgesRow>
          {hasDateInfo && (
            <AdoptionDate>
              {adoptionDate && `${t.adopted || 'Adopted'}: ${adoptionDate}`}
              {adoptionDate && abrogationDate && ' · '}
              {abrogationDate &&
                `${t.abrogated || 'Abrogated'}: ${abrogationDate}`}
            </AdoptionDate>
          )}
        </BannerRight>
      </BannerSection>

      {/* Segmented toolbar — "Development potential" (metrics + rules) and
          "Documents"; the Q&A button stays visible so "Ask about this zone"
          is always one click away. Skipped when nothing to switch to. */}
      {(showDocuments || showQA) && (
        <SegmentBar>
          {showDocuments && (
            <SegmentGroup>
              <Segment
                type="button"
                $active={activeSegment === 'potential'}
                onClick={() => setActiveSegment('potential')}
              >
                {t.segments?.potential}
              </Segment>
              <Segment
                type="button"
                $active={activeSegment === 'documents'}
                onClick={() => setActiveSegment('documents')}
              >
                {t.segments?.documents}
                {documentCount != null && documentCount > 0 && (
                  <SegmentCount $active={activeSegment === 'documents'}>
                    {documentCount}
                  </SegmentCount>
                )}
              </Segment>
            </SegmentGroup>
          )}
          {showQA && (
            <QAButton type="button" onClick={() => setIsQAModalOpen(true)}>
              <Icon name="sparkles" size="small" />
              {t.askQuestion || 'Ask me anything about this zone'}
            </QAButton>
          )}
        </SegmentBar>
      )}

      {/* Development potential — metrics table, then purpose / arrangement /
          references stacked below (default segment). */}
      <PaneWrap $active={activeSegment === 'potential'}>
        <PotentialPane>{potentialSlot}</PotentialPane>
        {/* Legend for the indices/ratios sits directly under the table. */}
        {legendSlot && <LegendSlot>{legendSlot}</LegendSlot>}
        {showPurposeRules && (
          <TrailingContext>
            <SectionDivider />
            <SectionLabel>
              <SectionLabelText>{t.segments?.purposeRules}</SectionLabelText>
              <SectionRule />
            </SectionLabel>

            {/* Purpose */}
            {hasPurpose &&
              (() => {
                const purposeHasReference = Boolean(
                  zone.purpose_sources || zone.purpose_reference
                )
                return (
                  <RegulationCard
                    $hasReference={purposeHasReference}
                    onClick={() => {
                      if (zone.purpose_sources) {
                        setModalState({
                          isOpen: true,
                          field: 'purpose',
                          reference: null,
                          value: purposeText,
                          defaultSource: zone.purpose_default_source,
                          sourcesAgreement: zone.purpose_sources_agreement,
                          sources: zone.purpose_sources,
                        })
                      } else if (zone.purpose_reference) {
                        setModalState({
                          isOpen: true,
                          field: 'purpose',
                          reference: zone.purpose_reference,
                        })
                      }
                    }}
                  >
                    <RegulationBody>
                      <RegulationLabel>
                        {t.purpose || 'Purpose'}
                      </RegulationLabel>
                      <RegulationValue>
                        {purposeText}
                        {zone.purpose_default_source && (
                          <MetricSourceBadge
                            source={zone.purpose_default_source}
                          />
                        )}
                      </RegulationValue>
                    </RegulationBody>
                    {purposeHasReference && (
                      <RegulationChevron>
                        <Icon name="chevron-right" size="small" />
                      </RegulationChevron>
                    )}
                  </RegulationCard>
                )
              })()}

            {/* Construction Arrangement */}
            {constructionArrangementText &&
              (() => {
                const arrangementHasReference = Boolean(
                  zone.construction_arrangement_sources ||
                  zone.construction_arrangement_reference
                )
                return (
                  <RegulationCard
                    $hasReference={arrangementHasReference}
                    onClick={() => {
                      if (zone.construction_arrangement_sources) {
                        setModalState({
                          isOpen: true,
                          field: 'construction_arrangement',
                          reference: null,
                          value: constructionArrangementText,
                          defaultSource:
                            zone.construction_arrangement_default_source,
                          sourcesAgreement:
                            zone.construction_arrangement_sources_agreement,
                          sources: zone.construction_arrangement_sources,
                        })
                      } else if (zone.construction_arrangement_reference) {
                        setModalState({
                          isOpen: true,
                          field: 'construction_arrangement',
                          reference: zone.construction_arrangement_reference,
                        })
                      }
                    }}
                  >
                    <RegulationBody>
                      <RegulationLabel>
                        {t.constructionArrangement ||
                          'Construction Arrangement'}
                      </RegulationLabel>
                      <RegulationValue>
                        {constructionArrangementText}
                        {zone.construction_arrangement_default_source && (
                          <MetricSourceBadge
                            source={
                              zone.construction_arrangement_default_source
                            }
                          />
                        )}
                      </RegulationValue>
                    </RegulationBody>
                    {arrangementHasReference && (
                      <RegulationChevron>
                        <Icon name="chevron-right" size="small" />
                      </RegulationChevron>
                    )}
                  </RegulationCard>
                )
              })()}

            {/* References — citations backing the rules above */}
            {hasReferences && (
              <>
                <SectionLabel $spaced>
                  <SectionLabelText>
                    {t.references || 'References'}
                  </SectionLabelText>
                  {(zone.references?.length || 0) > 0 && (
                    <SectionCount>{zone.references?.length}</SectionCount>
                  )}
                  <SectionRule />
                </SectionLabel>
                <ReferencesSection
                  references={zone.references!}
                  metricLabelMap={metricLabelMap}
                  onReferenceClick={(ref, page) => {
                    setRefModalState({
                      isOpen: true,
                      initialDocumentId: ref.documentId,
                      initialPage: page,
                    })
                  }}
                />
              </>
            )}
          </TrailingContext>
        )}
      </PaneWrap>

      {/* Documents — the full document library for this zone */}
      {showDocuments && (
        <PaneWrap $active={activeSegment === 'documents'}>
          <Pane>
            <DocumentsSection
              zone={zone}
              zoningType={zoningType}
              zoningService={zoningService}
              translations={documentTranslations}
              locale={locale}
              hideTitle
              onDocumentCount={setDocumentCount}
              municipalityName={zone.municipality_name}
              planName={planType}
              zoneName={zoneName}
            />
          </Pane>
        </PaneWrap>
      )}

      {/* Reference Modal */}
      {modalState.isOpen && (
        <MetricSourceModal
          isOpen={modalState.isOpen}
          metricLabel={
            modalState.field === 'purpose'
              ? t.purpose || 'Purpose'
              : t.constructionArrangement || 'Construction Arrangement'
          }
          value={modalState.value}
          defaultSource={modalState.defaultSource as any}
          sourcesAgreement={modalState.sourcesAgreement as any}
          sources={modalState.sources}
          geoPortalLink={(zone as any).geoportal_url}
          geoCenter={zone.geo_center}
          regionName={zone.region_name}
          municipalityName={zone.municipality_name}
          zoneName={planType}
          planName={zoneName}
          reference={modalState.reference || undefined}
          onClose={handleCloseModal}
        />
      )}

      {/* Reference Viewer Modal */}
      {hasReferences && refModalState.isOpen && (
        <ReferenceViewerModal
          isOpen={refModalState.isOpen}
          onClose={() => setRefModalState({ isOpen: false })}
          references={zone.references!}
          initialDocumentId={refModalState.initialDocumentId}
          initialPage={refModalState.initialPage}
          translations={referenceViewerTranslations}
          metricLabelMap={metricLabelMap}
          municipalityName={zone.municipality_name}
          planName={planType}
          zoneName={zoneName}
          getPdfProxyUrl={getPdfProxyUrl}
        />
      )}

      {/* Zone Q&A Modal */}
      {zoningType === 'general' &&
        zoneQAService &&
        zone.municipality_id &&
        zone.region_id && (
          <ZoneQAModal
            isOpen={isQAModalOpen}
            onClose={() => setIsQAModalOpen(false)}
            zoneContext={{
              zoneId: getZoneId(zone, zoningType) || '',
              zoneName: planType,
              municipalityId: zone.municipality_id,
              municipalityName: zone.municipality_name || '',
              regionId: zone.region_id,
              regionName: zone.region_name || '',
            }}
            zoningService={zoneQAService}
            language={(locale as ZoningQALanguage) || 'en'}
            translations={zoneQATranslations}
            zoneDocuments={zoneDocuments}
            onTranscribeVoice={onTranscribeVoice}
          />
        )}
    </HeaderContainer>
  )
}
